import React from 'react';
import { cn } from '../../utils/cn';

interface SwitchProps {
  id?: string;
  name?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  variant?: 'default' | 'small' | 'large';
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>((
  {
    id,
    name,
    checked,
    defaultChecked,
    onChange,
    onBlur,
    disabled = false,
    label,
    description,
    variant = 'default',
    className,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref
) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      // Always pass the event to onChange - react-hook-form expects the full event
      // Custom handlers can extract e.target.checked if needed
      (onChange as (event: React.ChangeEvent<HTMLInputElement>) => void)(e);
    }
  };

  const switchClasses = cn(
    'switch',
    {
      'w-4 h-4': variant === 'small',
      'w-5 h-5': variant === 'default',
      'w-6 h-6': variant === 'large',
      'opacity-50 cursor-not-allowed': disabled,
    },
    className
  );

  const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="flex items-center gap-3">
      <input
        ref={ref}
        type="checkbox"
        id={switchId}
        name={name}
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        className={switchClasses}
        aria-label={ariaLabel || label}
        aria-describedby={ariaDescribedBy}
        {...props}
      />
      
      {(label || description) && (
        <div className="flex-1">
          {label && (
            <label 
              htmlFor={switchId} 
              className="text-sm font-medium text-foreground cursor-pointer select-none"
            >
              {label}
            </label>
          )}
          {description && (
            <p className="text-sm text-muted-foreground mt-1">
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Switch.displayName = 'Switch';

export default Switch;